import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Captures the browser's `beforeinstallprompt` event so we can trigger
 * a native install dialog from our own UI (button, banner, etc.).
 *
 * Returns:
 *   canInstall  — true when the prompt is available
 *   isInstalled — true when the app is already running as a PWA
 *   install()   — triggers the native install dialog
 */
export default function useInstallPrompt() {
  const deferredPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Already running as installed PWA?
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    function onBeforeInstall(e) {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    }

    function onAppInstalled() {
      deferredPrompt.current = null;
      setCanInstall(false);
      setIsInstalled(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    const prompt = deferredPrompt.current;
    if (!prompt) return false;

    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    deferredPrompt.current = null;
    setCanInstall(false);

    return outcome === "accepted";
  }, []);

  return { canInstall, isInstalled, install };
}
